import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InsightsDomain, InsightsDrillResult, InsightsQueryFilters } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { Badge } from '../../components/ui/Badge.js';
import { drillInsights } from './api.js';

export interface DrillTarget {
  /** Omitted on pre-domain cards = 'time'. */
  domain?: InsightsDomain;
  filters: InsightsQueryFilters;
  title: string;
}

interface InsightsDrillModalProps {
  target: DrillTarget | null;
  onClose: () => void;
}

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** The underlying records behind a chart card/segment. Row click opens the record. */
export function InsightsDrillModal({ target, onClose }: InsightsDrillModalProps): JSX.Element | null {
  const [result, setResult] = useState<InsightsDrillResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return;
    setResult(null);
    setError(null);
    setLoading(true);
    let cancelled = false;
    drillInsights({ domain: target.domain ?? 'time', filters: target.filters })
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load the records.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  if (!target) return null;

  return (
    <Modal isOpen onClose={onClose} title={`Records — ${target.title}`} size="wide" fillHeight>
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : null}
      {error ? <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}
      {result?.domain === 'time' ? <TimeDrill result={result} /> : null}
      {result?.domain === 'tickets' ? <TicketDrill result={result} /> : null}
      {result?.domain === 'money' ? <InvoiceDrill result={result} /> : null}
      {result?.domain === 'pipeline' ? <OpportunityDrill result={result} /> : null}
    </Modal>
  );
}

function useRowNavigate(): (path: string) => (event: React.MouseEvent) => void {
  const navigate = useNavigate();
  return (path) => (event) => {
    if (event.ctrlKey || event.metaKey) window.open(path, '_blank', 'noopener');
    else navigate(path);
  };
}

/** In-cell link to a related record (customer/project/agreement) — never triggers the row. */
function EntityLink({ path, label }: { path: string; label: string }): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (event.ctrlKey || event.metaKey) window.open(path, '_blank', 'noopener');
        else navigate(path);
      }}
      className="max-w-full truncate text-left text-brand-700 hover:underline"
      title={label}
    >
      {label}
    </button>
  );
}

function customerPath(id: string): string {
  return `/customers?returnCustomerId=${encodeURIComponent(id)}`;
}

function DrillSummary({ shown, result }: { shown: number; result: InsightsDrillResult }): JSX.Element {
  return (
    <p className="mb-3 text-xs text-slate-500">
      {result.totalCount.toLocaleString()} records
      {result.totalHours !== undefined
        ? ` · ${result.totalHours.toLocaleString(undefined, { maximumFractionDigits: 1 })} hours total`
        : ''}
      {result.totalCount > shown ? ` · showing the ${shown} most recent` : ''}
    </p>
  );
}

function EmptyDrill(): JSX.Element {
  return <p className="py-8 text-center text-sm text-slate-500">No matching records.</p>;
}

function TimeDrill({ result }: { result: InsightsDrillResult }): JSX.Element {
  const open = useRowNavigate();
  const entries = result.entries ?? [];
  return (
    <div>
      <DrillSummary shown={entries.length} result={result} />
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1.5 pr-3 font-medium">Date</th>
            <th className="py-1.5 pr-3 font-medium">Member</th>
            <th className="py-1.5 pr-3 font-medium">Customer</th>
            <th className="py-1.5 pr-3 font-medium">Work item</th>
            <th className="py-1.5 pr-3 font-medium">Category</th>
            <th className="py-1.5 pr-3 text-right font-medium">Hours</th>
            <th className="py-1.5 pr-3 font-medium">Billable</th>
            <th className="py-1.5 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr
              key={entry.id}
              className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
              onClick={open(`/time-entries/${entry.id}`)}
            >
              <td className="whitespace-nowrap py-1.5 pr-3 text-slate-700">{entry.workDate}</td>
              <td className="max-w-36 truncate py-1.5 pr-3 text-slate-700">{entry.memberName}</td>
              <td className="max-w-40 truncate py-1.5 pr-3 text-slate-700">
                {entry.customerId && entry.customerName ? (
                  <EntityLink path={customerPath(entry.customerId)} label={entry.customerName} />
                ) : (
                  entry.customerName ?? '—'
                )}
              </td>
              <td
                className="max-w-56 truncate py-1.5 pr-3 text-slate-700"
                title={entry.ticketTitle ?? entry.projectName ?? entry.agreementName ?? ''}
              >
                {entry.ticketTitle ? (
                  entry.ticketTitle
                ) : entry.projectId && entry.projectName ? (
                  <EntityLink path={`/projects/${entry.projectId}`} label={entry.projectName} />
                ) : entry.agreementId && entry.agreementName ? (
                  <EntityLink path={`/agreements/${entry.agreementId}`} label={entry.agreementName} />
                ) : (
                  '—'
                )}
              </td>
              <td className="max-w-32 truncate py-1.5 pr-3 text-slate-700">
                {entry.serviceCategoryName ?? '—'}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-slate-900">
                {entry.hours.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </td>
              <td className="py-1.5 pr-3">
                <Badge tone={entry.billable ? 'green' : 'gray'}>
                  {entry.billable ? 'Billable' : 'Non-billable'}
                </Badge>
              </td>
              <td className="max-w-64 truncate py-1.5 text-slate-500" title={entry.notes ?? ''}>
                {entry.notes ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {entries.length === 0 ? <EmptyDrill /> : null}
    </div>
  );
}

function TicketDrill({ result }: { result: InsightsDrillResult }): JSX.Element {
  const open = useRowNavigate();
  const tickets = result.tickets ?? [];
  return (
    <div>
      <DrillSummary shown={tickets.length} result={result} />
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1.5 pr-3 font-medium">Ticket</th>
            <th className="py-1.5 pr-3 font-medium">Title</th>
            <th className="py-1.5 pr-3 font-medium">Customer</th>
            <th className="py-1.5 pr-3 font-medium">Status</th>
            <th className="py-1.5 pr-3 font-medium">Type</th>
            <th className="py-1.5 pr-3 font-medium">Priority</th>
            <th className="py-1.5 pr-3 font-medium">Assignees</th>
            <th className="py-1.5 pr-3 font-medium">Created</th>
            <th className="py-1.5 text-right font-medium">Age (days)</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr
              key={ticket.id}
              className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
              onClick={open(`/tickets/${ticket.id}`)}
            >
              <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-slate-900">
                {ticket.reference}
              </td>
              <td className="max-w-64 truncate py-1.5 pr-3 text-slate-700" title={ticket.title}>
                {ticket.title}
              </td>
              <td className="max-w-40 truncate py-1.5 pr-3 text-slate-700">
                {ticket.customerId && ticket.customerName ? (
                  <EntityLink path={customerPath(ticket.customerId)} label={ticket.customerName} />
                ) : (
                  ticket.customerName ?? '—'
                )}
              </td>
              <td className="py-1.5 pr-3">
                <Badge tone={ticket.closedAt ? 'gray' : 'green'}>{ticket.statusName}</Badge>
              </td>
              <td className="max-w-32 truncate py-1.5 pr-3 text-slate-700">{ticket.typeName ?? '—'}</td>
              <td className="py-1.5 pr-3 text-slate-700">{ticket.priority}</td>
              <td
                className="max-w-40 truncate py-1.5 pr-3 text-slate-700"
                title={ticket.assigneeNames.join(', ')}
              >
                {ticket.assigneeNames.length > 0 ? ticket.assigneeNames.join(', ') : '—'}
              </td>
              <td className="whitespace-nowrap py-1.5 pr-3 text-slate-700">{ticket.createdAt}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-900">
                {ticket.ageDays.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {tickets.length === 0 ? <EmptyDrill /> : null}
    </div>
  );
}

function InvoiceDrill({ result }: { result: InsightsDrillResult }): JSX.Element {
  const open = useRowNavigate();
  const invoices = result.invoices ?? [];
  return (
    <div>
      <DrillSummary shown={invoices.length} result={result} />
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1.5 pr-3 font-medium">Invoice</th>
            <th className="py-1.5 pr-3 font-medium">Customer</th>
            <th className="py-1.5 pr-3 font-medium">Agreement</th>
            <th className="py-1.5 pr-3 font-medium">Kind</th>
            <th className="py-1.5 pr-3 font-medium">Status</th>
            <th className="py-1.5 pr-3 font-medium">Issued</th>
            <th className="py-1.5 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr
              key={invoice.id}
              className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
              onClick={open(`/invoices/${invoice.id}`)}
            >
              <td className="whitespace-nowrap py-1.5 pr-3 font-medium text-slate-900">
                {invoice.number}
                {invoice.documentType === 'credit_memo' ? ' (credit)' : ''}
              </td>
              <td className="max-w-44 truncate py-1.5 pr-3 text-slate-700">
                <EntityLink path={customerPath(invoice.customerId)} label={invoice.customerName} />
              </td>
              <td className="max-w-44 truncate py-1.5 pr-3 text-slate-700">
                {invoice.agreementId && invoice.agreementName ? (
                  <EntityLink
                    path={`/agreements/${invoice.agreementId}`}
                    label={invoice.agreementName}
                  />
                ) : (
                  invoice.agreementName ?? '—'
                )}
              </td>
              <td className="py-1.5 pr-3 text-slate-700">{invoice.source.replace(/_/g, ' ')}</td>
              <td className="py-1.5 pr-3">
                <Badge tone={invoice.status === 'closed' ? 'blue' : 'green'}>{invoice.status}</Badge>
              </td>
              <td className="whitespace-nowrap py-1.5 pr-3 text-slate-700">
                {invoice.issueDate ?? '—'}
              </td>
              <td className="py-1.5 text-right tabular-nums text-slate-900">
                {CURRENCY.format(invoice.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {invoices.length === 0 ? <EmptyDrill /> : null}
    </div>
  );
}

function OpportunityDrill({ result }: { result: InsightsDrillResult }): JSX.Element {
  const open = useRowNavigate();
  const opportunities = result.opportunities ?? [];
  return (
    <div>
      <DrillSummary shown={opportunities.length} result={result} />
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1.5 pr-3 font-medium">Opportunity</th>
            <th className="py-1.5 pr-3 font-medium">Customer</th>
            <th className="py-1.5 pr-3 font-medium">Stage</th>
            <th className="py-1.5 pr-3 font-medium">Status</th>
            <th className="py-1.5 pr-3 font-medium">Sales rep</th>
            <th className="py-1.5 pr-3 font-medium">Type</th>
            <th className="py-1.5 pr-3 font-medium">Close date</th>
            <th className="py-1.5 text-right font-medium">Value</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((opp) => (
            <tr
              key={opp.id}
              className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
              onClick={open(`/sales/opportunities/${opp.id}`)}
            >
              <td className="max-w-64 truncate py-1.5 pr-3 font-medium text-slate-900" title={opp.name}>
                {opp.name}
              </td>
              <td className="max-w-40 truncate py-1.5 pr-3 text-slate-700">
                <EntityLink path={customerPath(opp.customerId)} label={opp.customerName} />
              </td>
              <td className="py-1.5 pr-3 text-slate-700">{opp.stage}</td>
              <td className="py-1.5 pr-3">
                <Badge tone={opp.status === 'Won' ? 'green' : opp.status.startsWith('Lost') ? 'red' : 'blue'}>
                  {opp.status}
                </Badge>
              </td>
              <td className="max-w-32 truncate py-1.5 pr-3 text-slate-700">{opp.salesRepName ?? '—'}</td>
              <td className="max-w-32 truncate py-1.5 pr-3 text-slate-700">
                {opp.serviceCategoryName ?? '—'}
              </td>
              <td className="whitespace-nowrap py-1.5 pr-3 text-slate-700">{opp.closeDate ?? '—'}</td>
              <td className="py-1.5 text-right tabular-nums text-slate-900">
                {CURRENCY.format(opp.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {opportunities.length === 0 ? <EmptyDrill /> : null}
    </div>
  );
}
